// web/src/pages/admin/EmailTemplatesManagerPage.tsx
//
// A11 — Email templates manager: token palette, live preview, version
// history/restore, and a "customize a transactional email" picker.
// Replaces the generic-lookup registration for `/admin/email-templates`.
import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  emailTemplatesApi,
  type EmailTemplateDto,
  type EmailTemplateCatalogEntry,
} from "@/lib/email-templates";
import { emailTemplateAssetsApi, type EmailTemplateAsset } from "@/lib/email-template-assets";
import { ApiError } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { RowActions } from "@/components/row-actions";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { EmailComposer } from "@/components/email/EmailComposer";
import { sanitizeEmailHtml } from "@/lib/sanitize-email";
import { formatDateTime } from "@/lib/format";
import { useConfirm } from "@/hooks/use-confirm";
import { toast } from "@/hooks/use-toast";

function serverMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
  }
  return fallback;
}

interface FormState {
  name: string;
  subject: string;
  htmlBody: string;
  sortOrder: number;
  templateKey: string | null;
}

function emptyForm(templateKey: string | null = null, subject = "", htmlBody = ""): FormState {
  return { name: "", subject, htmlBody, sortOrder: 0, templateKey };
}

function dtoToForm(dto: EmailTemplateDto): FormState {
  return {
    name: dto.name,
    subject: dto.subject,
    htmlBody: dto.htmlBody,
    sortOrder: dto.sortOrder,
    templateKey: dto.templateKey ?? null,
  };
}

/** Starting-point body for a freshly-picked transactional key: a simple reference paragraph per token. */
function tokenReferenceBody(tokens: EmailTemplateCatalogEntry["tokens"]): string {
  return tokens.map((t) => `<p>${t.name}: {{${t.name}}}</p>`).join("") || "<p></p>";
}

/** Task B4 — one entry of the template's inline-image manifest: a CID paired with the library asset it resolves to at send. */
interface InlineImageRef {
  cid: string;
  assetId: number;
}

function parseManifest(json: string | null | undefined): InlineImageRef[] {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as InlineImageRef[]) : [];
  } catch {
    return [];
  }
}

export function EmailTemplatesManagerPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<EmailTemplateDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [previewData, setPreviewData] = useState<{ subject: string; htmlBody: string } | null>(null);
  // Task B4 — the in-progress inline-image manifest for the template being edited;
  // reset on open/close, mutated only when the user inserts an image "from library".
  const [manifest, setManifest] = useState<InlineImageRef[]>([]);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  const { data: page, isLoading } = useQuery({
    queryKey: ["email-templates"],
    queryFn: () => emailTemplatesApi.list(),
  });
  const rows = page?.items ?? [];

  const { data: catalog = [] } = useQuery({
    queryKey: ["email-templates-catalog"],
    queryFn: () => emailTemplatesApi.catalog(),
  });

  const { data: libraryAssets = [] } = useQuery({
    queryKey: ["email-template-assets"],
    queryFn: () => emailTemplateAssetsApi.list(),
    enabled: imagePickerOpen,
  });

  const selectedCatalogEntry = form.templateKey
    ? catalog.find((c) => c.templateKey === form.templateKey) ?? null
    : null;

  const { data: history = [] } = useQuery({
    queryKey: ["email-template-history", editing?.id],
    queryFn: () => emailTemplatesApi.history(editing!.id),
    enabled: historyOpen && editing != null,
  });

  function openNewSnippet() {
    setEditing(null);
    setForm(emptyForm(null));
    setPreviewData(null);
    setHistoryOpen(false);
    setManifest([]);
    setEditorOpen(true);
  }

  function openCustomize(entry: EmailTemplateCatalogEntry) {
    setEditing(null);
    setForm({
      name: entry.templateKey,
      subject: "",
      htmlBody: tokenReferenceBody(entry.tokens),
      sortOrder: 0,
      templateKey: entry.templateKey,
    });
    setPreviewData(null);
    setHistoryOpen(false);
    setPickerOpen(false);
    setManifest([]);
    setEditorOpen(true);
  }

  function openEdit(t: EmailTemplateDto) {
    setEditing(t);
    setForm(dtoToForm(t));
    setPreviewData(null);
    setHistoryOpen(false);
    setManifest(parseManifest(t.inlineImageManifestJson));
    setEditorOpen(true);
  }

  /** Task B4 — insert a library asset into the body as a CID `<img>` and record it on the manifest. */
  function insertFromLibrary(asset: EmailTemplateAsset) {
    const cid = `img${asset.id}`;
    setManifest((m) => (m.some((r) => r.assetId === asset.id) ? m : [...m, { cid, assetId: asset.id }]));
    setForm((f) => ({ ...f, htmlBody: `${f.htmlBody}<img src="cid:${cid}" data-cid="${cid}">` }));
    setImagePickerOpen(false);
  }

  function closeEditor() {
    setEditorOpen(false);
    setImagePickerOpen(false);
    setEditing(null);
    setHistoryOpen(false);
    setPreviewData(null);
    setManifest([]);
  }

  const save = useMutation({
    mutationFn: async () => {
      const inlineImageManifestJson = manifest.length > 0 ? JSON.stringify(manifest) : null;
      if (editing) {
        return emailTemplatesApi.patch(editing.id, {
          name: form.name,
          subject: form.subject,
          htmlBody: form.htmlBody,
          sortOrder: form.sortOrder,
          deleted: editing.deleted,
          templateKey: form.templateKey,
          inlineImageManifestJson,
        });
      }
      return emailTemplatesApi.create({
        name: form.name,
        subject: form.subject,
        htmlBody: form.htmlBody,
        sortOrder: form.sortOrder,
        templateKey: form.templateKey,
        inlineImageManifestJson,
      });
    },
    onSuccess: (dto) => {
      void qc.invalidateQueries({ queryKey: ["email-templates"] });
      void qc.invalidateQueries({ queryKey: ["email-templates-catalog"] });
      toast.success(editing ? "Template updated." : "Template created.");
      setEditing(dto);
    },
    onError: (err: unknown) => toast.error(serverMessage(err, "Save failed.")),
  });

  const del = useMutation({
    mutationFn: (id: number) => emailTemplatesApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email-templates"] });
      toast.success("Template deleted.");
      closeEditor();
    },
    onError: (err: unknown) => toast.error(serverMessage(err, "Delete failed.")),
  });

  const preview = useMutation({
    mutationFn: (id: number) => emailTemplatesApi.preview(id, { sampleEntityId: null }),
    onSuccess: (result) => setPreviewData(result),
    onError: (err: unknown) => toast.error(serverMessage(err, "Preview failed.")),
  });

  const restore = useMutation({
    mutationFn: ({ id, auditLogId }: { id: number; auditLogId: number }) =>
      emailTemplatesApi.restore(id, auditLogId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email-templates"] });
      toast.success("Template restored.");
      closeEditor();
    },
    onError: (err: unknown) => toast.error(serverMessage(err, "Restore failed.")),
  });

  async function handleDelete(t: EmailTemplateDto) {
    const ok = await confirm({
      title: "Delete template?",
      body: t.name,
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (ok) del.mutate(t.id);
  }

  function appendTokenToSubject(tokenName: string) {
    setForm((f) => ({ ...f, subject: `${f.subject}{{${tokenName}}}` }));
  }

  const cols: DataGridColumn<EmailTemplateDto>[] = [
    { id: "name", header: "Name", accessor: (r) => r.name },
    {
      id: "templateKey",
      header: "Template key",
      accessor: (r) =>
        r.templateKey ? (
          <Badge variant="info">{r.templateKey}</Badge>
        ) : (
          <Badge variant="outline">Snippet</Badge>
        ),
    },
    { id: "subject", header: "Subject", accessor: (r) => r.subject },
    {
      id: "lastEdited",
      header: "Last edited",
      accessor: (r) =>
        r.modifiedOn
          ? `${formatDateTime(r.modifiedOn)}${r.modifiedByName ? ` · ${r.modifiedByName}` : ""}`
          : "—",
    },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex justify-end pr-2">
          <RowActions
            onEdit={() => openEdit(r)}
            onDelete={() => void handleDelete(r)}
            deleteDisabled={del.isPending}
            deleteTooltip="Delete Template"
          />
        </div>
      ),
      align: "right",
    },
  ];

  const uncustomized = catalog.filter((c) => !c.hasOverride);

  return (
    <>
      <ListPage backTo="/admin"
        title="Email Templates"
        primaryAction={
          <div className="flex gap-2">
            <IntentButton
              intent="secondary"
              onClick={() => setPickerOpen(true)}
              aria-label="Customize a transactional email"
            >
              Customize a transactional email
            </IntentButton>
            <IntentButton onClick={openNewSnippet} aria-label="Add snippet">
              <Plus className="mr-2 h-4 w-4" /> Add Snippet
            </IntentButton>
          </div>
        }
      >
        <DataGrid
          rows={rows}
          columns={cols}
          rowKey={(r) => r.id}
          totalRows={rows.length}
          pageIndex={0}
          pageSize={rows.length || 1}
          loading={isLoading}
          emptyHeadline="No email templates found."
          onPageChange={() => void 0}
        />
      </ListPage>

      {/* Picker: customize a transactional email */}
      <DetailModal
        open={pickerOpen}
        onOpenChange={(o) => setPickerOpen(o)}
        title="Customize a transactional email"
      >
        <div className="space-y-2 p-4">
          {uncustomized.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              All transactional emails already have an override.
            </p>
          ) : (
            uncustomized.map((entry) => (
              <button
                key={entry.templateKey}
                type="button"
                className="flex w-full items-center justify-between rounded-md border p-2 text-left text-sm hover:bg-slate-50"
                onClick={() => openCustomize(entry)}
              >
                <span>{entry.templateKey}</span>
                <Badge variant="outline">{entry.tokens.length} tokens</Badge>
              </button>
            ))
          )}
        </div>
      </DetailModal>

      {/* Editor */}
      <DetailModal
        open={editorOpen}
        onOpenChange={(o) => {
          if (!o) closeEditor();
        }}
        title={
          editing
            ? "Edit Template"
            : form.templateKey
              ? `Customize: ${form.templateKey}`
              : "New Snippet"
        }
      >
        <div className="space-y-4 p-4">
          <div>
            <Label htmlFor="et-name">Name *</Label>
            <Input
              id="et-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>

          {selectedCatalogEntry && (
            <div>
              <Label>Token palette</Label>
              <div className="flex flex-wrap gap-1 pt-1">
                {selectedCatalogEntry.tokens.map((tok) => (
                  <button
                    key={tok.name}
                    type="button"
                    title={tok.description}
                    onClick={() => appendTokenToSubject(tok.name)}
                    className="rounded-full border px-2 py-0.5 text-xs hover:bg-slate-100"
                  >
                    {`{{${tok.name}}}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="et-subject">Subject *</Label>
            <Input
              id="et-subject"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Body</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setImagePickerOpen(true)}
              >
                Insert image from library
              </Button>
            </div>
            <EmailComposer
              value={form.htmlBody}
              onChange={(html) => setForm((f) => ({ ...f, htmlBody: html }))}
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending || !form.name || !form.subject}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="outline"
              onClick={() => editing && preview.mutate(editing.id)}
              disabled={!editing || preview.isPending}
            >
              {preview.isPending ? "Loading…" : "Preview"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setHistoryOpen((o) => !o)}
              disabled={!editing}
            >
              History
            </Button>
            <Button variant="outline" onClick={closeEditor}>
              Cancel
            </Button>
          </div>

          {previewData && (
            <div className="rounded-md border p-3">
              <p className="text-sm font-medium">{previewData.subject}</p>
              <div
                className="prose prose-sm mt-2 max-w-none"
                dangerouslySetInnerHTML={{
                  __html: sanitizeEmailHtml(previewData.htmlBody, { allowRemoteImages: false }),
                }}
              />
            </div>
          )}

          {historyOpen && editing && (
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">History</p>
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground">No history yet.</p>
              ) : (
                history.map((h) => (
                  <div
                    key={h.auditLogId}
                    className="flex items-center justify-between border-b py-1 text-sm last:border-0"
                  >
                    <div>
                      <p>{h.subject}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(h.timestampUtc)}
                        {h.byName ? ` · ${h.byName}` : ""}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        restore.mutate({ id: editing.id, auditLogId: h.auditLogId })
                      }
                      disabled={restore.isPending}
                    >
                      Restore
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </DetailModal>

      {/* Task B4 — insert-from-library picker */}
      <DetailModal
        open={imagePickerOpen}
        onOpenChange={(o) => setImagePickerOpen(o)}
        title="Insert Image From Library"
      >
        <div className="space-y-2 p-4">
          {libraryAssets.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No inline images yet — upload some on the{" "}
              <Link to="/admin/email-templates/inline-images" className="underline">
                Inline Images
              </Link>{" "}
              page.
            </p>
          ) : (
            libraryAssets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                className="flex w-full items-center justify-between rounded-md border p-2 text-left text-sm hover:bg-slate-50"
                onClick={() => insertFromLibrary(asset)}
              >
                <span>{asset.fileName}</span>
                <Badge variant="outline">
                  {asset.width != null && asset.height != null ? `${asset.width}×${asset.height}` : "—"}
                </Badge>
              </button>
            ))
          )}
        </div>
      </DetailModal>
    </>
  );
}
